import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Create axios instance with default config
const adminApi = axios.create({
  baseURL: `${API_URL}/api/v1/admin`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add token
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

const adminService = {
  // Authentication
  login: async (email, password) => {
    try {
      const response = await adminApi.post('/login', { email, password });
      if (response.data.success) {
        localStorage.setItem('adminToken', response.data.data.token);
        localStorage.setItem('adminUser', JSON.stringify(response.data.data.user));
      }
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Login failed' };
    }
  },

  logout: async () => {
    try {
      await adminApi.post('/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
    }
  },

  // Profile Management
  getProfile: async () => {
    const response = await adminApi.get('/profile');
    return response.data;
  },

  updateProfile: async (profileData) => {
    const response = await adminApi.put('/profile', profileData);
    return response.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await adminApi.post('/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  },

  requestPasswordReset: async (email) => {
    const response = await adminApi.post('/request-password-reset', { email });
    return response.data;
  },

  resetPassword: async (token, newPassword) => {
    const response = await adminApi.post('/reset-password', { token, newPassword });
    return response.data;
  },

  // Dashboard
  getDashboardData: async () => {
    const response = await adminApi.get('/dashboard');
    return response.data;
  },

  // Admin Management
  getAdmins: async () => {
    const response = await adminApi.get('/admins');
    return response.data;
  },

  getAdminById: async (id) => {
    const response = await adminApi.get(`/admins/${id}`);
    return response.data;
  },

  createAdmin: async (adminData) => {
    const response = await adminApi.post('/admins', adminData);
    return response.data;
  },

  updateAdminStatus: async (adminId, status) => {
    const response = await adminApi.patch(`/admins/${adminId}/status`, { status });
    return response.data;
  },

  updateAdminRole: async (adminId, role) => {
    const response = await adminApi.patch(`/admins/${adminId}/role`, { role });
    return response.data;
  },

  deleteAdmin: async (adminId) => {
    const response = await adminApi.delete(`/admins/${adminId}`);
    return response.data;
  },

  // Invitation
  acceptInvitation: async (token, password) => {
    const response = await adminApi.post('/accept-invitation', { token, password });
    return response.data;
  },

  // Auth Utilities
  isAuthenticated: () => {
    const token = localStorage.getItem('adminToken');
    const user = localStorage.getItem('adminUser');
    return !!(token && user);
  },

  getRoles: async () => {
    const response = await adminApi.get('/roles');
    return response.data;
  },

  getRolePermissions: async (roleId) => {
    const response = await adminApi.get(`/roles/${roleId}/permissions`);
    return response.data;
  },

  updateRolePermissions: async (roleId, permissions) => {
    const response = await adminApi.put(`/roles/${roleId}/permissions`, { permissions });
    return response.data;
  },

  getAdminPermissions: async (adminId) => {
    const response = await adminApi.get(`/admins/${adminId}/permissions`);
    return response.data;
  },

  // Permission check utility
  hasPermission: (requiredPermission) => {
    const user = adminService.getCurrentUser();
    if (!user) return false;
    
    // Super Admin always has permission
    if (user.role === 'Super Admin') return true;
    
    // Check user permissions
    const [section, permission] = requiredPermission.split('.');
    return user.permissions?.[section]?.[permission] || false;
  },

  // Get all permissions for current user
  getCurrentUserPermissions: () => {
    const user = adminService.getCurrentUser();
    return user?.permissions || {};
  },

  getCurrentUser: () => {
    const userStr = localStorage.getItem('adminUser');
    return userStr ? JSON.parse(userStr) : null;
  },

  getToken: () => {
    return localStorage.getItem('adminToken');
  },

  setAuthData: (token, user) => {
    localStorage.setItem('adminToken', token);
    localStorage.setItem('adminUser', JSON.stringify(user));
  },

  clearAuthData: () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
  },

  // Health Check
  healthCheck: async () => {
    try {
      const response = await axios.get(`${API_URL}/health`);
      return response.data;
    } catch (error) {
      throw { message: 'Server is unavailable' };
    }
  },
};

export default adminService;